import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";
import { Spinner } from "../Spinner/Spinner";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 gap-2 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground hover:bg-n-95 [&_.spinner]:fill-neutral-100 [&_.spinner]:text-neutral-500",
        /** Design-system name for the ink-filled button; same rendering as `default`. */
        primary:
          "bg-primary text-primary-foreground hover:bg-n-95 [&_.spinner]:fill-neutral-100 [&_.spinner]:text-neutral-500",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-n-20 dark:hover:bg-n-30 [&_.spinner]:fill-neutral-100 [&_.spinner]:text-neutral-500",
        ghost:
          "bg-transparent text-n-80 hover:bg-accent hover:text-accent-foreground",
        accent:
          "bg-rust text-paper hover:bg-[color-mix(in_oklab,var(--rust)_85%,var(--ink))] [&_.spinner]:fill-neutral-100 [&_.spinner]:text-neutral-500",
        /**
         * TipTap-style toolbar ghost (`tiptap-button` + `data-style="ghost"`): muted default,
         * stronger hover, pressed/open uses lighter tint + primary-colored icons like TipTap active.
         * Drive pressed state with `data-active-state="on"` and/or Radix `data-state="open"` on the trigger.
         */
        toolbarGhost:
          "hover:bg-accent hover:text-accent-foreground" +
          "data-[active-state=on]:bg-muted/50 data-[active-state=on]:text-foreground " +
          "data-[active-state=on]:[&_svg]:text-teal dark:data-[active-state=on]:[&_svg]:text-teal data-[active-state=on]:hover:bg-muted " +
          "data-[state=open]:bg-muted/50 data-[state=open]:text-foreground " +
          "data-[state=open]:[&_svg]:text-primary data-[state=open]:hover:bg-muted",
        link: "bg-transparent text-rust underline decoration-[color-mix(in_oklab,var(--rust)_45%,transparent)] underline-offset-[0.22em] hover:text-[color-mix(in_oklab,var(--rust)_78%,var(--ink))] hover:decoration-rust",
      },
      size: {
        default: "h-10 px-4 py-2",
        xs: "h-6 px-2 text-xs [&_svg]:size-3",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        xl: "h-12 rounded-md px-10",
        icon: "h-10 w-10",
        /** Design-system compact icon control (28px square, 14px icon); chat toolbars. */
        "icon-sm": "h-7 min-h-7 w-7 p-0 rounded-sm [&_svg]:size-3.5",
        /** TipTap default toolbar icon control: 2rem square, ~1rem icon (`toolbarGhost` pairs well). */
        toolbarIcon:
          "h-7 min-h-7 w-7 min-w-7 shrink-0 gap-1 rounded-full p-2 text-sm [&_svg]:size-4",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      isLoading = false,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }), {
          "cursor-pointer": !isLoading && !props.disabled,
        })}
        ref={ref}
        disabled={isLoading || props.disabled}
        {...props}
      >
        {isLoading ? (
          <div className="relative inline-flex items-center justify-start">
            <Spinner className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
            <span
              className={cn(
                "relative inline-flex items-center justify-start whitespace-nowrap gap-2 transition-opacity",
                {
                  "opacity-0": isLoading,
                },
              )}
            >
              {children}
            </span>
          </div>
        ) : (
          children
        )}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
